import {
  Box,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { memo, useMemo } from 'react';

import { translationLib } from 'entities/translation';

import { CoverageCell, CoverageLanguage, EEntityType, LanguageListItem } from 'shared/api';

type CoverageSortKey = 'language' | keyof Omit<CoverageLanguage, 'language'>;

type TypeColumn = {
  key: keyof Omit<CoverageLanguage, 'language' | 'overall'>;
  type: EEntityType;
  label: string;
};

const TYPE_COLUMNS: TypeColumn[] = [
  { key: 'disease', type: EEntityType.Disease, label: 'Disease' },
  { key: 'diseasePets', type: EEntityType.DiseasePets, label: 'Disease Pets' },
  { key: 'vaccine', type: EEntityType.Vaccine, label: 'Vaccine' },
  { key: 'vaccinePets', type: EEntityType.VaccinePets, label: 'Vaccine Pets' },
  { key: 'breed', type: EEntityType.Breed, label: 'Breed' },
];

const PALETTE = {
  success: { fill: '#c8e6c9', text: '#1b5e20' },
  warning: { fill: '#fff3c4', text: '#7a5b00' },
  error: { fill: '#ffcdd2', text: '#7a1a1a' },
} as const;

type Props = {
  coverage: CoverageLanguage[];
  languages: LanguageListItem[];
  isLoading: boolean;
  selectedType?: EEntityType | null;
  selectedLanguage?: string | null;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSortChange: (key: CoverageSortKey) => void;
  onCellClick: (type: EEntityType, language: string) => void;
  onLanguageClick: (language: string) => void;
};

const overallTotals = (rows: CoverageLanguage[]) =>
  rows.reduce(
    (acc, row) => {
      acc.translated += row.overall.translated;
      acc.total += row.overall.total;
      return acc;
    },
    { translated: 0, total: 0 },
  );

const renderCell = (cell: CoverageCell, isOverall = false) => {
  const band = translationLib.percentColorBand(cell.percent);
  const colors = PALETTE[band];
  const width = Math.max(0, Math.min(100, cell.percent));
  return (
    <Box
      sx={{
        position: 'relative',
        p: 1,
        minWidth: 110,
        background: isOverall ? '#f7f7f7' : 'transparent',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          width: `${width}%`,
          background: colors.fill,
          opacity: 0.5,
        },
      }}
    >
      <Typography variant="body2" sx={{ position: 'relative', fontWeight: 600 }}>
        {translationLib.formatPercent(cell.percent)}
      </Typography>
      <Typography variant="caption" sx={{ position: 'relative', color: 'text.secondary' }}>
        {translationLib.formatCellRatio(cell.translated, cell.total)}
      </Typography>
    </Box>
  );
};

export const TranslationCoverage = memo(
  ({
    coverage,
    languages,
    isLoading,
    selectedType,
    selectedLanguage,
    sortBy,
    sortOrder,
    onSortChange,
    onCellClick,
    onLanguageClick,
  }: Props) => {
    const langMeta = useMemo(() => {
      const map = new Map<string, LanguageListItem>();
      languages.forEach((l) => map.set(l.code, l));
      return map;
    }, [languages]);

    const totals = useMemo(() => overallTotals(coverage), [coverage]);
    const overallPercent = totals.total ? (totals.translated / totals.total) * 100 : 0;

    const sortedCoverage = useMemo(() => {
      if (!sortBy) return coverage;
      const dir = sortOrder === 'asc' ? 1 : -1;
      const nameOf = (row: CoverageLanguage) => (langMeta.get(row.language)?.name ?? row.language).toLowerCase();
      return [...coverage].sort((a, b) => {
        if (sortBy === 'language') {
          return nameOf(a).localeCompare(nameOf(b)) * dir;
        }
        const cellKey = sortBy as Exclude<keyof CoverageLanguage, 'language'>;
        const diff = a[cellKey].percent - b[cellKey].percent;
        // Percent ties are common (many 100% / 99.6%), so keep a stable name tiebreak.
        return diff !== 0 ? diff * dir : nameOf(a).localeCompare(nameOf(b));
      });
    }, [coverage, sortBy, sortOrder, langMeta]);

    const sortDirection = (key: CoverageSortKey): 'asc' | 'desc' => (sortBy === key ? sortOrder : 'asc');

    return (
      <Paper sx={{ width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Typography variant="h6">Translation coverage</Typography>
          <Typography variant="body2" color="text.secondary">
            Overall: {translationLib.formatPercent(overallPercent)} (
            {translationLib.formatCellRatio(totals.translated, totals.total)})
          </Typography>
        </Box>

        <Box sx={{ width: '100%', height: 4 }}>{isLoading && <LinearProgress />}</Box>

        <Box
          sx={{
            opacity: isLoading ? 0.6 : 1,
            transition: 'opacity 0.2s',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TableContainer sx={{ flex: 1, minHeight: 0 }}>
            <Table stickyHeader size="small" aria-label="translation coverage matrix">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      background: '#fafafa',
                      minWidth: 160,
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === 'language'}
                      direction={sortDirection('language')}
                      onClick={() => onSortChange('language')}
                    >
                      Language
                    </TableSortLabel>
                  </TableCell>
                  {TYPE_COLUMNS.map((col) => (
                    <TableCell
                      key={col.key}
                      align="left"
                      sx={{
                        background: selectedType === col.type ? '#eaf7f4' : '#fafafa',
                      }}
                    >
                      <TableSortLabel
                        active={sortBy === col.key}
                        direction={sortDirection(col.key)}
                        onClick={() => onSortChange(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell sx={{ background: '#f0f0f0' }}>
                    <TableSortLabel
                      active={sortBy === 'overall'}
                      direction={sortDirection('overall')}
                      onClick={() => onSortChange('overall')}
                    >
                      Overall
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coverage.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={TYPE_COLUMNS.length + 2} align="center">
                      No coverage data
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedCoverage.map((row) => {
                    const meta = langMeta.get(row.language);
                    const isSelectedLang = selectedLanguage === row.language;
                    return (
                      <TableRow
                        key={row.language}
                        sx={{
                          background: isSelectedLang ? 'rgba(41, 177, 148, 0.06)' : 'transparent',
                        }}
                      >
                        <TableCell
                          onClick={() => onLanguageClick(row.language)}
                          sx={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            background: isSelectedLang ? '#eaf7f4' : '#fff',
                            cursor: 'pointer',
                            '&:hover': { background: 'action.hover' },
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {meta?.name ?? row.language}{' '}
                            <Box component="span" sx={{ color: 'text.disabled' }}>
                              ({row.language})
                            </Box>
                          </Typography>
                          {meta?.nativeName && (
                            <Typography variant="caption" color="text.secondary">
                              {meta.nativeName}
                            </Typography>
                          )}
                        </TableCell>
                        {TYPE_COLUMNS.map((col) => {
                          const cell = row[col.key];
                          const isSelected = isSelectedLang && selectedType === col.type;
                          return (
                            <TableCell
                              key={col.key}
                              onClick={() => onCellClick(col.type, row.language)}
                              sx={{
                                p: 0,
                                cursor: 'pointer',
                                border: isSelected ? '2px solid' : undefined,
                                borderColor: isSelected ? 'primary.main' : undefined,
                                '&:hover': { background: 'action.hover' },
                              }}
                            >
                              {renderCell(cell)}
                            </TableCell>
                          );
                        })}
                        <TableCell sx={{ p: 0 }}>{renderCell(row.overall, true)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>
    );
  },
);

TranslationCoverage.displayName = 'TranslationCoverage';
